import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import DatePicker from "react-datepicker"
import { Dropdown } from 'react-bootstrap'
import XLSX from 'xlsx'

import { httpOk, GET } from 'helpers/api'
import { formatMoney, returnSign, formatDate } from 'helpers/helpers'
import CustomTooltip from "pages/parts/CustomTooltip"
import SummaryGrossIncome from "./summary-modals/SummaryGrossIncome"
import SummaryExpense from "./summary-modals/SummaryExpense"
import SummaryDebtPaid from "./summary-modals/SummaryDebtPaid"
import { endOfMonth, format, startOfMonth } from "date-fns"
import useDidMountEffect from "pages/parts/useDidMountEffect"

function Summary() {
	const { t } = useTranslation()
	const date = new Date()

	const [data, setData] = useState([])
	const [overall, setOverall] = useState({})
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterData, setFilterData] = useState({
		start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
		type: null,
		random: 0,
		random_active_modal: 0,
	})
	const [activeModal, setActiveModal] = useState(null)

	async function searchWithFilter() {
		var filterUrl = `/report/summary`
		var urlParams = ""

		if (filterData.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterData.start_date

		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setOverall(response.data.total)
			setData(response.data.data)
		}

		setDropdownIsOpen(false)
	}

	function handleSelectedDate(type, month = true, date) {
		if (month) {
			setFilterData({
				...filterData,
				'start_date': format(startOfMonth(new Date(filterData.start_date)), 'yyyy-MM-dd'),
				'end_date': format(endOfMonth(new Date(filterData.start_date)), 'yyyy-MM-dd'),
				'type': type,
				'random_active_modal': filterData.random_active_modal + 1,
			})
		} else {
			setFilterData({
				...filterData,
				'start_date': date,
				'end_date': date,
				'type': type,
				'random_active_modal': filterData.random_active_modal + 1,
			})
		}
	}

	async function exportToExcel() {
		var excelHeaders = [[
			t('date'),
			t('good_reception'), t('supplier_amount'),
			t('cash'), t('terminal'), t('debt'), t('usd'),
			t('sales'), t('returned'), t('discount_amount'), t('gross_income'),
			t('expenses'), t('employee_paid'), t('supplier_paid'), t('debt_paid'), t('net_profit'),
		]]
		// ⚠️ Quyidagi 2 ta ro'yxat POZITSION — ustun qo'shilsa uchalasi ham
		//    bir vaqtda yangilanishi shart, aks holda sarlavha va qiymat siljiydi
		//    (xato bermaydi, jimgina noto'g'ri fayl chiqadi).

		for (let i = 0; i < data.length; i++) {
			var excelItems = []
			excelItems.push(data[i].days)
			excelItems.push(Number(data[i]['documents_in_total_amount']))
			excelItems.push(Number(data[i]['documents_in_total_amount_usd']))
			excelItems.push(Number(data[i]['cash_total_amount']))
			excelItems.push(Number(data[i]['terminal_total_amount']))
			excelItems.push(Number(data[i]['debt_total_amount']))
			excelItems.push(Number(data[i]['usd_cash_total_amount']))
			excelItems.push(Number(data[i]['total_sales']))
			excelItems.push(Number(data[i]['total_returned']))
			excelItems.push(Number(data[i]['total_discount_amount']))
			excelItems.push(Number(data[i]['gross_income']))
			excelItems.push(Number(data[i]['total_expenses']))
			excelItems.push(Number(data[i]['employee_paid']))
			excelItems.push(Number(data[i]['supplier_paid_usd']))
			excelItems.push(Number(data[i]['total_debt_paid']))
			excelItems.push(Number(data[i]['net_profit']))
			excelHeaders.push(excelItems)
		}

		excelHeaders.push([
			t('total'),
			Number(overall['documents_in_total_amount']),
			Number(overall['documents_in_total_amount_usd']),
			Number(overall['cash_total_amount']),
			Number(overall['terminal_total_amount']),
			Number(overall['debt_total_amount']),
			Number(overall['usd_cash_total_amount']),
			Number(overall['total_sales']),
			Number(overall['total_returned']),
			Number(overall['total_discount_amount']),
			Number(overall['gross_income']),
			Number(overall['total_expenses']),
			Number(overall['employee_paid']),
			Number(overall['supplier_paid_usd']),
			Number(overall['total_debt_paid']),
			Number(overall['net_profit']),
		])

		const ws = XLSX.utils.aoa_to_sheet(excelHeaders);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "SheetJS");
		XLSX.writeFile(wb, t('summary_report') + ".xlsx");
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	useEffect(() => {
		searchWithFilter()
	}, [filterData.random]) // eslint-disable-line react-hooks/exhaustive-deps

	useDidMountEffect(() => {
		setActiveModal(filterData.type)
	}, [filterData.random_active_modal]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header">
				<h4 className="vertical-center h-100">
					{t('summary_report')}
				</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex gap-2">
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('date')}</label>
												<div className="calendar-input">
													<DatePicker
														showMonthYearPicker
														showFullMonthYearPicker
														className="form-control"
														dateFormat="dd.MM.yyyy"
														selected={filterData.start_date ? new Date(filterData.start_date) : ''}
														onChange={(date) => setFilterData({ ...filterData, 'start_date': formatDate(date, 'yyyy-MM-dd') })} />
													<i className="uil uil-calendar-alt"></i>
												</div>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary"
														onClick={() => setDropdownIsOpen(false)}>
														{t('cancel')}
													</button>
													<button className="btn btn-primary"
														onClick={() => searchWithFilter(false)}>
														{t('apply')}
													</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div>
								<CustomTooltip text={t('EXCEL')}>
									<button className="btn btn-outline-primary btn-wide-2"
										onClick={() => exportToExcel()}>
										<i className="uil uil-file-download-alt"></i>
									</button>
								</CustomTooltip>
							</div>
						</div>
					</div>

					<div className="table-responsive table-fixed mb-3" style={{ minHeight: 'calc(100vh - 225px)' }}>
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th>{t('date')}</th>
									<th style={{ 'minWidth': '70px' }} className="text-end td-ellipsis" title={t('good_reception')}>{t('good_reception')}</th>
									<th style={{ 'minWidth': '70px' }} className="text-end td-ellipsis" title={t('supplier_amount')}>{t('supplier_amount')}</th>
									<th style={{ 'minWidth': '70px' }} className="text-end td-ellipsis" title={t('cash')}>{t('cash')}</th>
									<th style={{ 'minWidth': '70px' }} className="text-end td-ellipsis" title={t('terminal')}>{t('terminal')}</th>
									<th style={{ 'minWidth': '70px' }} className="text-end td-ellipsis" title={t('debt')}>{t('debt')}</th>
									<th style={{ 'minWidth': '70px' }} className="text-end td-ellipsis" title={t('usd')}>{t('usd')}</th>
									<th style={{ 'minWidth': '70px' }} className="text-end td-ellipsis" title={t('sales')}>{t('sales')}</th>
									<th style={{ 'minWidth': '70px' }} className="text-end td-ellipsis" title={t('returned')}>{t('returned')}</th>
								<th style={{ 'minWidth': '70px' }} className="text-end td-ellipsis" title={t('discount_amount')}>{t('discount_amount')}</th>
									<th style={{ 'minWidth': '70px' }} className="text-end td-ellipsis cursor" title={t('gross_income')}>{t('gross_income')}</th>
									<th style={{ 'minWidth': '70px' }} className="text-end td-ellipsis cursor" title={t('expenses')}>{t('expenses')}</th>
									{/* Xodimga berilgan — «Ходимлар ҳисоб-китоби» bo'limidan.
									    Xarajatdan ajratilgan, lekin Sof foydadan baribir ayiriladi. */}
									<th style={{ 'minWidth': '70px' }} className="text-end td-ellipsis" title={t('employee_paid')}>{t('employee_paid')}</th>
								<th style={{ 'minWidth': '70px' }} className="text-end td-ellipsis" title={t('supplier_paid')}>{t('supplier_paid')}</th>
									<th style={{ 'minWidth': '70px' }} className="text-end td-ellipsis cursor" title={t('debt_paid')}>{t('debt_paid')}</th>
									<th style={{ 'minWidth': '70px' }} className="text-end td-ellipsis" title={t('net_profit')}>{t('net_profit')}</th>
								</tr>
							</thead>
							<tbody>
								{data?.map((item, index) => (
									<tr key={index}>
										<td>{formatDate(item.days)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.documents_in_total_amount)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.documents_in_total_amount_usd)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.cash_total_amount)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.terminal_total_amount)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.debt_total_amount)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.usd_cash_total_amount)}</td>
										<td className="text-end text-nowrap">{formatMoney(item.total_sales)}</td>
										<td className="text-end text-nowrap">{Number(item.total_returned) ? <span className="text-danger">-{formatMoney(item.total_returned)}</span> : 0}</td>
									<td className="text-end text-nowrap">{formatMoney(item.total_discount_amount)}</td>
										<td className="text-end text-nowrap cursor text-primary"
											onClick={() => handleSelectedDate('gross_income', false, item.days)}>
											{formatMoney(item.gross_income)}
										</td>
										<td className="text-end text-nowrap cursor text-danger"
											onClick={() => handleSelectedDate('expenses', false, item.days)}>
											{formatMoney(item.total_expenses)}
										</td>
										<td className="text-end text-nowrap" style={{ color: '#7c3aed' }}>
											{formatMoney(item.employee_paid)}
										</td>
										<td className="text-end text-nowrap" style={{ color: '#c9740a' }}>
											{Number(item.supplier_paid_usd) ? formatMoney(item.supplier_paid_usd) + ' $' : 0}
										</td>
										<td className="text-end text-nowrap cursor text-success"
											onClick={() => handleSelectedDate('debt_paid', false, item.days)}>
											{formatMoney(item.total_debt_paid)}
										</td>
										{/* Rang qiymatga qarab: manfiy foyda YASHIL chiqishi chalg'itardi */}
										<td className={'text-end text-nowrap fw-bold ' + (Number(item.net_profit) < 0 ? 'text-danger' : 'text-success')}>
											{formatMoney(item.net_profit)}
										</td>
									</tr>
								))}
							</tbody>
							<tfoot>
								{data.length > 0 && (
									<tr className="tr-bold table-secondary">
										<th>{t('total')}</th>
										<th className="text-end text-nowrap">{formatMoney(overall.documents_in_total_amount)}</th>
										<th className="text-end text-nowrap">{formatMoney(overall.documents_in_total_amount_usd)}</th>
										<th className="text-end text-nowrap">{formatMoney(overall.cash_total_amount)}</th>
										<th className="text-end text-nowrap">{formatMoney(overall.terminal_total_amount)}</th>
										<th className="text-end text-nowrap">{formatMoney(overall.debt_total_amount)}</th>
										<th className="text-end text-nowrap">{formatMoney(overall.usd_cash_total_amount)}</th>
										<th className="text-end text-nowrap">{formatMoney(overall.total_sales)}</th>
										<th className="text-end text-nowrap text-danger">{Number(overall.total_returned) ? '-'+formatMoney(overall.total_returned) : 0}</th>
									<th className="text-end text-nowrap">{formatMoney(overall.total_discount_amount)}</th>
										<th className="text-end text-nowrap">{formatMoney(overall.gross_income)}</th>
										<th className="text-end text-nowrap">{formatMoney(overall.total_expenses)}</th>
										<th className="text-end text-nowrap" style={{ color: '#7c3aed' }}>{formatMoney(overall.employee_paid)}</th>
									<th className="text-end text-nowrap" style={{ color: '#c9740a' }}>{Number(overall.supplier_paid_usd) ? formatMoney(overall.supplier_paid_usd) + ' $' : 0}</th>
										<th className="text-end text-nowrap">{formatMoney(overall.total_debt_paid)}</th>
										<th className={'text-end text-nowrap fw-bold ' + (Number(overall.net_profit) < 0 ? 'text-danger' : 'text-success')}>{formatMoney(overall.net_profit)}</th>
									</tr>
								)}
							</tfoot>
						</table>
					</div>
				</div>
			</div>

			{activeModal === 'gross_income' &&
				<SummaryGrossIncome
					setActiveModal={setActiveModal}
					filterData={filterData} />
			}
			{activeModal === 'expenses' &&
				<SummaryExpense
					setActiveModal={setActiveModal}
					filterData={filterData} />
			}
			{activeModal === 'debt_paid' &&
				<SummaryDebtPaid
					setActiveModal={setActiveModal}
					filterData={filterData} />
			}
		</>
	)
}

export default Summary
